import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useProductSettingsContext } from '../context/ProductSettingsContext';
import { PutProductDistributorFulfillmentType } from '../hooks/useFetchProductSettingsDistributorOverrides';
import { SkuFulfillmentMethod } from '../../../Enums/SkuFulfillmentMethod';
import { toYyyyMmDdInput, isSunday, maxInMarketDate } from '../utils/inMarketDate';

export const DistributorInMarketDatePicker = ({ row, handleUpdateRow }) => {
    const { getDistributorFloorDate, itemInMarketDate, itemEndInMarketDate, showInMarketDateError } = useProductSettingsContext();
    const isReplenishable = row.skuFulfillmentType?.id === SkuFulfillmentMethod.REPLENISHABLE;
    const isOrderOnly = row.skuFulfillmentType?.id === SkuFulfillmentMethod.ORDER_ONLY;
    // The SKU must have its own default in-market date before a distributor-level override can be set.
    const disabled = row?.legal === false || (!isReplenishable && !isOrderOnly) || !itemInMarketDate;
    const value = toYyyyMmDdInput(row.inMarketDateOverride);
    const min = getDistributorFloorDate(row);
    const weekLimit = maxInMarketDate();
    // Tightest upper bound: the item's own end in-market date (a start cannot fall after the end) and
    // the 52-week limit (date strings sort chronologically).
    const max = [itemEndInMarketDate, weekLimit].filter(Boolean).sort()[0];

    const handleChange = async (newValue) => {
        let apiDate;
        if (newValue === null) {
            apiDate = null;
        } else {
            if (!newValue.isValid()) return;
            const selected = newValue.format('YYYY-MM-DD');
            // Guard against typed input that bypasses the disabled calendar days.
            if (!isSunday(selected)) {
                showInMarketDateError('In-market date must be a Sunday.');
                return;
            }
            if (min && selected < min) return;
            if (selected > max) return;
            apiDate = selected;
        }

        const updatedRow = {
            ...row,
            inMarketDateOverride: apiDate ? `${apiDate}T00:00:00` : null,
            inMarketDateIsDefault: !apiDate
        };

        const result = await PutProductDistributorFulfillmentType(
            row.product,
            { ...updatedRow, skuFulfillmentType: row.skuFulfillmentType },
            null,
            { setInMarketDate: true, inMarketDate: apiDate }
        );
        if (result.ok) {
            await handleUpdateRow(updatedRow);
        } else {
            showInMarketDateError(result.error || 'The in-market date could not be saved.');
        }
    };

    return (
        <DatePicker
            value={value ? dayjs(value) : null}
            disabled={disabled}
            onChange={handleChange}
            minDate={min ? dayjs(min) : undefined}
            maxDate={dayjs(max)}
            shouldDisableDate={(date) => date.day() !== 0}
            slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true }
            }}
        />
    );
};

DistributorInMarketDatePicker.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func
};
